import { type InvitationStatus, ROLES, type Role } from '@account-manager/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface Invitation {
  id: string;
  email: string;
  role: Role;
  status: InvitationStatus;
  expires_at: string;
  created_at: string;
}

export function Invitations() {
  const { t } = useTranslation('settings');
  const { activeOrgId } = useAuth();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('Bookkeeper');
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['invitations', activeOrgId],
    queryFn: () => api<{ invitations: Invitation[] }>(`/orgs/${activeOrgId}/invitations`),
    enabled: !!activeOrgId,
  });

  const invite = useMutation({
    mutationFn: () =>
      api(`/orgs/${activeOrgId}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      }),
    onSuccess: () => {
      setEmail('');
      qc.invalidateQueries({ queryKey: ['invitations'] });
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/orgs/${activeOrgId}/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invitations'] }),
  });

  const resend = useMutation({
    mutationFn: (id: string) =>
      api(`/orgs/${activeOrgId}/invitations/${id}/resend`, { method: 'POST' }),
  });

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold">{t('invitations')}</h1>
      <div className="mb-6 max-w-md rounded border bg-white p-4">
        <h2 className="mb-2 font-semibold">{t('inviteMember')}</h2>
        <input
          type="email"
          placeholder="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-2 w-full rounded border px-3 py-2"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          className="mb-2 w-full rounded border px-3 py-2"
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="button"
          onClick={() => invite.mutate()}
          className="rounded bg-blue-600 px-4 py-2 text-white"
        >
          {t('inviteMember')}
        </button>
      </div>

      <h2 className="mb-2 font-semibold">{t('pendingInvitations')}</h2>
      <table className="w-full bg-white">
        <thead className="border-b text-left text-sm text-slate-600">
          <tr>
            <th className="p-2">Email</th>
            <th className="p-2">{t('role')}</th>
            <th className="p-2">Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.data?.invitations.map((i) => (
            <tr key={i.id} className="border-b text-sm">
              <td className="p-2">{i.email}</td>
              <td className="p-2">{i.role}</td>
              <td className="p-2">{i.status}</td>
              <td className="p-2">
                {i.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      onClick={() => resend.mutate(i.id)}
                      className="mr-2 text-xs text-blue-600"
                    >
                      {t('resendInvite')}
                    </button>
                    <button
                      type="button"
                      onClick={() => revoke.mutate(i.id)}
                      className="text-xs text-red-600"
                    >
                      {t('revokeInvite')}
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
